import React, { useEffect, useState, useRef, useCallback } from "react";
import { db, auth } from "./firebaseConfig";
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { DatePicker, Select } from "antd";
import dayjs from "dayjs"; // Helpful for working with dates
import StyledLineChart from './/StyledLineChart';
import './StyledLineChart.css';
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ItemsSoldView from "./ItemsSoldView";
import BusyTimesView from "./BusyTimesView";
import logUserAction from './utils/logUserAction.js';
import { ChevronDownIcon } from "@heroicons/react/24/solid";


const SalesReportPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const paymentMethods = ["Visa", "Mastercard", "Debit", "Amex", "Cash"];
  const [selectedPayments, setSelectedPayments] = useState([...paymentMethods]); // All checked by default
  const [user, setUser] = useState(null);
  const [delayDone, setDelayDone] = useState(false);
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [selectedStore, setSelectedStore] = useState("__none__");
  const { Option } = Select;
  const [selectedRange, setSelectedRange] = useState(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [startDateManuallySet, setStartDateManuallySet] = useState(false);
  const [activeView, setActiveView] = useState("chart");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [showStore, setShowStore] = useState(true);
  const [showDateRange, setShowDateRange] = useState(false);


  const storeOptions = [
    { label: "Select Store", value: "__none__", disabled: true },
    { label: "All Stores", value: "" },
    { label: "Jean-Talon O", value: "Jean-Talon O" },
    { label: "Jean-Talon Market", value: "Jean-Talon Market" },
    { label: "Events", value: "Events" },
    { label: "Test", value: "Test" }
  ];

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });

  const handleDateShift = (direction) => {
    const multiplier = direction === "next" ? 1 : -1;

    let newStart = dayjs(startDate);
    let newEnd = dayjs(endDate);

    switch (selectedRange) {
      case "Today":
        newStart = newStart.add(multiplier, "day");
        newEnd = newEnd.add(multiplier, "day");
        break;
      case "Week":
        newStart = newStart.add(multiplier, "week");
        newEnd = newEnd.add(multiplier, "week");
        break;
      case "Month":
        newStart = newStart.add(multiplier, "month").startOf("month");
        newEnd = newStart.endOf("month");
        break;
      case "Year":
        newStart = newStart.add(multiplier, "year").startOf("year");
        newEnd = newStart.endOf("year");
        break;
      default:
        return;
    }

    setStartDate(newStart.toDate());
    setEndDate(newEnd.toDate());
  };

  const handleGenerateQuickBooksReport = () => {
    if (!startDate || !endDate) return;

    // Filter orders for the specific day
    const dailyOrders = filteredOrders.filter(order => {
      const orderDate = dayjs(order.timestamp);
      return orderDate.isSame(dayjs(startDate), 'day');
    });

    if (dailyOrders.length === 0) {
      alert("No transactions found for the selected day.");
      return;
    }

    // Generate the IIF content
    const iifContent = generateIIFContent(dailyOrders);

    // Trigger download of the IIF file
    const blob = new Blob([iifContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const formattedDate = dayjs(startDate).format('YYYY-MM-DD');
    link.href = url;
    link.download = `QuickBooksReport_${formattedDate}.iif`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const generateIIFContent = (orders) => {
    let cardSales = 0, cardGST = 0, cardPST = 0;
    let cashSales = 0, cashGST = 0, cashPST = 0;

    const cardPaymentTypes = ['Visa', 'Mastercard', 'Debit', 'Amex'];

    orders.forEach(order => {
      const paymentMethods = order.paymentMethods || [{ method: order.paymentMethod, amount: order.grandTotal }];
      const subtotal = parseFloat(order.subtotal || 0);
      const gst = parseFloat(order.gst || 0);
      const pst = parseFloat(order.qst || 0);
      const grandTotal = parseFloat(order.grandTotal || 1); // Avoid division by 0

      let amountRemaining = grandTotal;

      for (const pm of paymentMethods) {
        const method = pm.method;
        const originalAmount = parseFloat(pm.amount || 0);
        const amountToApply = Math.min(originalAmount, amountRemaining);

        // Cap to remaining balance
        const ratio = amountToApply / grandTotal;

        const partSales = subtotal * ratio;
        const partGST = gst * ratio;
        const partPST = pst * ratio;

        if (cardPaymentTypes.includes(method)) {
          cardSales += partSales;
          cardGST += partGST;
          cardPST += partPST;
        } else if (method === 'Cash') {
          cashSales += partSales;
          cashGST += partGST;
          cashPST += partPST;
        }

        amountRemaining -= amountToApply;
        if (amountRemaining <= 0) break; 
      }
    });

    const today = dayjs(startDate).format('MM/DD/YYYY');
    let iif = '';

    // Cards Journal Entry
    const cardTotal = cardSales + cardGST + cardPST;
    if (cardTotal > 0) {
      iif += '!TRNS\tTRNSTYPE\tDATE\tACCNT\tAMOUNT\tNAME\tMEMO\n';
      iif += '!SPL\tTRNSTYPE\tDATE\tACCNT\tAMOUNT\tNAME\tMEMO\n';
      iif += `TRNS\tGENERAL JOURNAL\t${today}\tSCOTIA BANK NEW\t${cardTotal.toFixed(2)}\t\tCard Totals\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\tSales\t-${cardSales.toFixed(2)}\tCards\tSales\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\t25500 · GST Payable\t-${cardGST.toFixed(2)}\tGST\tGST Collected\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\t25610 · PST Payable\t-${cardPST.toFixed(2)}\tPST\tPST Collected\n`;
      iif += 'ENDTRNS\n\n';
    }

    // Cash Journal Entry
    const cashTotal = cashSales + cashGST + cashPST;
    if (cashTotal > 0) {
      iif += '!TRNS\tTRNSTYPE\tDATE\tACCNT\tAMOUNT\tNAME\tMEMO\n';
      iif += '!SPL\tTRNSTYPE\tDATE\tACCNT\tAMOUNT\tNAME\tMEMO\n';
      iif += `TRNS\tGENERAL JOURNAL\t${today}\tCash in Drawer\t${cashTotal.toFixed(2)}\t\tCash Totals\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\tSales\t-${cashSales.toFixed(2)}\tCash\tSales\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\t25500 · GST Payable\t-${cashGST.toFixed(2)}\tGST\tGST Collected\n`;
      iif += `SPL\tGENERAL JOURNAL\t${today}\t25610 · PST Payable\t-${cashPST.toFixed(2)}\tPST\tPST Collected\n`;
      iif += 'ENDTRNS\n\n';
    }

    return iif;
  };

  const handleQuickRange = useCallback((range) => {
    setStartDateManuallySet(false);
    setSelectedRange(range);
    const today = dayjs();

    switch (range) {
      case "Today":
        setStartDate(today.startOf('day').toDate());
        setEndDate(today.endOf('day').toDate());
        break;
      case "Week":
        setStartDate(today.startOf('week').add(1, 'day').toDate());
        setEndDate(today.endOf('week').add(1, 'day').toDate());
        break;
      case "Month":
        setStartDate(today.startOf('month').toDate());
        setEndDate(today.endOf('month').toDate());
        break;
      case "Year":
        setStartDate(today.startOf('year').toDate());
        setEndDate(today.endOf('year').toDate());
        break;
      case "All Time":
        if (orders.length > 0) {
          const sortedOrders = [...orders].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
          const firstDate = sortedOrders[0]?.timestamp;
          const lastDate = sortedOrders[sortedOrders.length - 1]?.timestamp;

          const validFirst = dayjs(firstDate);
          const validLast = dayjs(lastDate);

          if (validFirst.isValid() && validLast.isValid()) {
            setStartDate(validFirst.startOf('day').toDate());
            setEndDate(validLast.endOf('day').toDate());
          } else {
            console.error("Invalid dates in All Time range:", validFirst, validLast);
          }
        } else {
          console.warn("No orders available for All Time range.");
        }
        break;
      default:
        break;
    }
  }, [orders]);

  const LoadingSpinner = () => (
    <div className="fixed inset-0 flex justify-center items-center bg-white z-50">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-500"></div>
    </div>
  );



  useEffect(() => {
    const timer = setTimeout(() => setDelayDone(true), 1000); // 1 second delay
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (orders.length > 0 && !startDate && !endDate) {
      const sortedOrders = [...orders].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );

      const firstDate = sortedOrders[0]?.timestamp;
      const lastDate = sortedOrders[sortedOrders.length - 1]?.timestamp;

      if (firstDate && lastDate) {
        setStartDate(firstDate);
        setEndDate(lastDate);
      }
    }
  }, [orders, startDate, endDate]);

  useEffect(() => {
    if (
      orders.length > 0 &&
      !selectedRange &&
      !startDateManuallySet
    ) {
      handleQuickRange("Today");
    }
  }, [orders, selectedRange, handleQuickRange, startDateManuallySet]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);

    try {
      const ordersRef = collection(db, "orders");

      // ✅ If "No store selected" (user cleared selection), show nothing!
      if (selectedStore === "__none__") {
        setOrders([]); // Clear orders entirely!
        return; // Exit early.
      }

      // ✅ If "All Stores" (empty string ""), query all orders.
      const q = selectedStore !== ""
        ? query(ordersRef, where("store", "==", selectedStore)) // Specific store
        : ordersRef; // All stores (no filter)

      const querySnapshot = await getDocs(q);

      const fetchedOrders = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        let timestamp = null;

        // ✅ Try preferred field first
        if (data.timestamp?.toDate) {
          timestamp = data.timestamp.toDate();
        } else if (data.timestamp instanceof Date) {
          timestamp = data.timestamp;
        } else if (typeof data.timestamp === "string" || typeof data.timestamp === "number") {
          timestamp = new Date(data.timestamp);
        }

        // ✅ Fallback to createdAt or clientCreatedAt if missing/invalid
        if (!timestamp || isNaN(timestamp.getTime())) {
          if (data.createdAt?.toDate) {
            timestamp = data.createdAt.toDate();
          } else if (data.clientCreatedAt?.toDate) {
            timestamp = data.clientCreatedAt.toDate();
          }
        }

        // ✅ Final validation
        if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
          console.warn("Skipping invalid order timestamp", data);
          return;
        }

        fetchedOrders.push({
          ...data,
          timestamp,
        });
      });

      setOrders(fetchedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]); // ✅ No warning now!  

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setAuthChecked(true);
        return;
      }

      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          setUser(null);
        } else {
          const userData = userDocSnap.data();

          if (userData.admin === true) {
            setUser({
              ...currentUser,
              role: "Admin",
              store: userData.store || '',
              approved: userData.approved,
              specialAccess: userData.specialAccess,
            });
          } else if (userData.role === "Owner" && userData.approved) {
            setUser({
              ...currentUser,
              role: "Owner",
              store: userData.store || '',
              approved: true,
              specialAccess: false,
            });
            setSelectedStore(userData.store);
          } else {
            setUser(null);
          }

          // ✅ Log once per session
          if (!sessionStorage.getItem("loggedInOnce")) {
            sessionStorage.setItem("loggedInOnce", "true");
          }
        }

      } catch (error) {
        console.error("Error fetching user info:", error);
        setUser(null);
      }

      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);


  useEffect(() => {
  }, [orders]);


  const togglePaymentMethod = (method) => {
    setSelectedPayments((prevSelected) => {
      if (prevSelected.includes(method)) {
        return prevSelected.filter((m) => m !== method);
      } else {
        return [...prevSelected, method];
      }
    });
  };

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setSelectedOrder(null);
  };

  const [transactionsToShow, setTransactionsToShow] = useState(20);

  const handleLoadMore = () => {
    setTransactionsToShow((prev) => prev + 20);
  };

  const handleLoadAll = () => {
    setTransactionsToShow(filteredOrders.length);
  };

  useEffect(() => {
    setTransactionsToShow(20);
  }, [filteredOrders]);

  useEffect(() => {
    if (user && selectedStore !== "__none__") {
      logUserAction(user, "Viewed Sales Report", {
        store: selectedStore || user.store,
        range: selectedRange || "Custom",
      });
    }
  }, [user, selectedStore, selectedRange]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeDetails();
      }
    };

    if (showDetailsModal) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDetailsModal]);

  const applyFilters = useCallback(() => {

    // ✅ If NO store selected, show nothing
    // ✅ If the user cleared the selection (null), show nothing.
    if (selectedStore === null) {
      setFilteredOrders([]);
      return;
    }

    // ✅ If a specific store is selected (non-empty), filter by store
    let filtered = [...orders];
    if (selectedStore !== "") {
      filtered = filtered.filter(order => order.store === selectedStore);
    }


    // ✅ Filter by selected payment methods
    if (selectedPayments.length > 0) {
      filtered = filtered.filter((order) => {
        if (order.paymentMethods?.length) {
          return order.paymentMethods.some(pm => selectedPayments.includes(pm.method));
        }
        return selectedPayments.includes(order.paymentMethod);
      });
    } else {
      filtered = [];
    }

    // ✅ Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((order) => {
        const orderDate = order.timestamp instanceof Date
          ? order.timestamp
          : new Date(order.timestamp);

        if (isNaN(orderDate)) {
          console.warn("Skipping order with invalid date:", order);
          return false;
        }

        return orderDate >= start && orderDate <= end;
      });
    }

    setFilteredOrders(filtered);
  }, [orders, selectedPayments, startDate, endDate, selectedStore]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    // When custom dates are selected (manual RangePicker)
    if (!selectedRange && startDate && endDate) {
      applyFilters();
    }
  }, [startDate, endDate, selectedRange, applyFilters]);

  const calculateTotals = () => {
    let subtotal = 0, gst = 0, qst = 0, grandTotal = 0;
    filteredOrders.forEach((order) => {
      subtotal += parseFloat(order.subtotal || 0);
      gst += parseFloat(order.gst || 0);
      qst += parseFloat(order.qst || 0);
      grandTotal += parseFloat(order.grandTotal || 0);
    });
    return { subtotal, gst, qst, grandTotal };
  };

  const { subtotal, gst, qst, grandTotal } = calculateTotals();

  const { RangePicker } = DatePicker;

  if (!delayDone || !authChecked) {
    return <LoadingSpinner />;
  }

  if (!user && delayDone && authChecked) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-80px)]">
        <div className="bg-white border border-gray-300 shadow-lg rounded-lg p-8 max-w-sm w-full text-center">
          <div className="flex justify-center mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mb-4" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">Access Denied</h2>
          <p className="text-gray-600 mb-6">You do not have the required permissions to view this page.</p>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full overflow-x-hidden max-w-full">
      {/* Toggle Button for mobile */}
      <button
        className="fixed top-1/2 left-0 -translate-y-1/2 z-[9999] h-48 w-3 bg-tifsu border border-gray-500 rounded-r-full shadow md:hidden flex items-center justify-center"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
      </button>

      {/* ✅ Filters Sidebar */}
      <div className={`bg-gray-100 overflow-y-auto transition-transform duration-300
        fixed md:static z-50 top-[60px] left-0
        h-[calc(100vh-60px)] md:h-auto
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
        w-full md:w-72 md:translate-x-0 p-4 space-y-6`}>
        <div className="bg-indigo-500 text-white rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3">View</h3>
          <div className="flex flex-col gap-2">
            {["chart", "items", "busy"].map((view) => (
              <button
                key={view}
                className={`w-full text-left px-4 py-2 rounded font-medium transition
                  ${activeView === view
                    ? "bg-white text-indigo-500"
                    : "bg-indigo-400 hover:bg-indigo-300 text-white"
                  }`}
                onClick={() => setActiveView(view)}
              >
                {view === "chart"
                  ? "📊 Sales Overview"
                  : view === "items"
                    ? "🍪 Top Items"
                    : "⏰ Peak Hours"}
              </button>
            ))}
          </div>
        </div>
        <h2 className="text-xl font-bold mb-4">Filters</h2>

        {/* Collapsible: Payment Methods */}
        <div className="flex flex-col items-start space-y-2 mb-6">
          <button
            onClick={() => setShowPayments(prev => !prev)}
            className="flex justify-between items-center w-full"
          >
            <h3 className="font-semibold">Payment Methods</h3>
            <ChevronDownIcon
              className={`h-5 w-5 transition-transform duration-300 ${showPayments ? "rotate-180" : ""
                }`}
            />
          </button>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${showPayments ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
              }`}
          >
            <div className="grid grid-cols-3 gap-2 w-full mt-2">
              {paymentMethods.map((method) => {
                const isSelected = selectedPayments.includes(method);
                return (
                  <button
                    key={method}
                    type="button"
                    onClick={() => togglePaymentMethod(method)}
                    className={`flex flex-col items-center justify-center rounded-lg border transition aspect-rectangle
              ${isSelected
                        ? "bg-[#c1c6fc] border-indigo-400"
                        : "bg-white border-gray-300 hover:bg-gray-100"}`}
                  >
                    <img
                      src={`/icons/${method}.png`}
                      alt={method}
                      className="h-8 w-8 object-contain"
                    />
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2 mt-4 pt-3">
              <button
                className="px-4 py-1 border border-gray-300 rounded hover:border-indigo-500 text-sm"
                onClick={() => setSelectedPayments([...paymentMethods])}
              >
                Select All
              </button>
              <button
                className="px-4 py-1 border border-gray-300 rounded hover:border-indigo-500 text-sm"
                onClick={() => setSelectedPayments([])}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Collapsible: Store Selector (Admin Only) */}
        {user?.role === "Admin" && (
          <div className="flex flex-col gap-2 mb-6">
            <button
              onClick={() => setShowStore(prev => !prev)}
              className="flex justify-between items-center w-full"
            >
              <label className="font-semibold">Select Store</label>
              <ChevronDownIcon
                className={`h-5 w-5 transition-transform duration-300 ${showStore ? "rotate-180" : ""
                  }`}
              />
            </button>

            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${showStore ? "max-h-[300px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
              <Select
                value={selectedStore}
                onChange={(value) => {
                  setSelectedStore(value);

                  if (value === "__none__") {
                    setOrders([]);
                    setFilteredOrders([]);
                    setShowDateRange(false); // Hide Date Picker if "__none__"
                  } else {
                    setShowDateRange(true); // ✅ Show Date Picker when a store is selected
                  }
                }}
                showSearch
                allowClear
                style={{ width: "100%", marginTop: "0.5rem" }}
                placeholder="Select a store"
              >
                {storeOptions.map((store) => (
                  <Option key={store.label} value={store.value} disabled={store.disabled}>
                    {store.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {/* Collapsible: Date Range */}
        <div className="flex flex-col gap-2 w-full mb-6">
          <button
            onClick={() => setShowDateRange(prev => !prev)}
            className="flex justify-between items-center w-full"
          >
            <h3 className="font-semibold">Select Date Range</h3>
            <ChevronDownIcon
              className={`h-5 w-5 transition-transform duration-300 ${showDateRange ? "rotate-180" : ""
                }`}
            />
          </button>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${showDateRange ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
              }`}
          >
            <div className="flex flex-wrap gap-2 mt-2">
              {["Today", "Week", "Month", "Year", "All Time"].map((label) => {
                const isSelected = selectedRange === label;
                return (
                  <button
                    key={label}
                    onClick={() => handleQuickRange(label)}
                    className={`px-3 py-1 text-sm rounded border whitespace-nowrap transition
              ${isSelected
                        ? "bg-indigo-500 text-white border-indigo-600"
                        : "bg-indigo-100 text-black hover:bg-indigo-200 border-indigo-300"}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <RangePicker
              onChange={(dates) => {
                setSelectedRange(null);
                setStartDateManuallySet(true);
                if (!dates || dates.length !== 2) {
                  setStartDate(null);
                  setEndDate(null);
                } else {
                  const [start, end] = dates;
                  const validStart = dayjs(start);
                  const validEnd = dayjs(end);
                  if (validStart.isValid() && validEnd.isValid()) {
                    setStartDate(validStart.startOf("day").toDate());
                    setEndDate(validEnd.endOf("day").toDate());
                  }
                }
              }}
              allowClear
              format="YYYY-MM-DD"
              value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
              style={{ width: "100%", marginTop: "1rem" }}
            />
          </div>
        </div>

      </div>

      {/* ✅ Main Content Area */}
      <div className={`transition-all duration-300 p-4 w-full max-w-full ${sidebarOpen ? "ml-72" : "ml-0"} md:ml-0`}>
        <h1 className="text-3xl font-bold text-center mb-6">Sales Report</h1>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 text-center mb-8">
          <div className="p-2 md:p-4 bg-gray-100 rounded">
            <p className="text-sm md:text-lg font-bold">Subtotal</p>
            <p className="text-sm md:text-base">
              ${(subtotal - (filteredOrders.reduce((sum, o) => sum + parseFloat(o.discountValue || 0), 0))).toFixed(2)}
            </p>
          </div>
          <div className="p-2 md:p-4 bg-gray-100 rounded">
            <p className="text-sm md:text-lg font-bold">GST</p>
            <p className="text-sm md:text-base">${gst.toFixed(2)}</p>
          </div>
          <div className="p-2 md:p-4 bg-gray-100 rounded">
            <p className="text-sm md:text-lg font-bold">QST</p>
            <p className="text-sm md:text-base">${qst.toFixed(2)}</p>
          </div>
          <div className="p-2 md:p-4 bg-indigo-100 rounded flex flex-col items-center justify-center relative">
            <p className="text-sm md:text-lg font-bold">Grand Total</p>
            <p className="text-sm md:text-base">${grandTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Conditional Views */}
        {selectedStore !== "__none__" && (
          <div className="w-full mt-8 space-y-4" ref={chartRef}>
            {/* Shared: Prev / Next Date Controls */}
            <div className="relative flex items-center justify-between w-full gap-x-2 px-2 mb-4 min-h-[40px] min-w-0">
              {["Today", "Week", "Month", "Year"].includes(selectedRange) && (
                <button
                  onClick={() => handleDateShift("prev")}
                  className="absolute left-0 bg-gray-200 hover:bg-gray-300 text-sm shadow px-4 py-1 rounded flex items-center gap-2"
                >
                  {/* Mobile arrow */}
                  <span className="md:hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </span>

                  {/* Desktop label */}
                  <span className="hidden md:block">
                    {`Previous ${selectedRange === "Today" ? "Day" : selectedRange || "Day"}`}
                  </span>
                </button>
              )}

              <div className="flex-1 text-center font-semibold text-gray-800 px-2 text-[clamp(1rem,3vw,1.5rem)] truncate">
                {selectedRange === "Today" && dayjs(startDate).format("MMMM D, YYYY")}
                {selectedRange === "Week" && `${dayjs(startDate).format("MMMM D")} – ${dayjs(endDate).format("MMMM D, YYYY")}`}
                {selectedRange === "Month" && dayjs(startDate).format("MMMM YYYY")}
                {selectedRange === "Year" && dayjs(startDate).format("YYYY")}
                {selectedRange === "All Time" && `${dayjs(startDate).format("MMMM D, YYYY")} – ${dayjs(endDate).format("MMMM D, YYYY")}`}
                {!selectedRange && `${dayjs(startDate).format("MMMM D, YYYY")} – ${dayjs(endDate).format("MMMM D, YYYY")}`}
              </div>

              {selectedRange !== "All Time" && (
                <button
                  onClick={() => handleDateShift("next")}
                  className="absolute right-0 bg-gray-200 hover:bg-gray-300 text-sm shadow px-4 py-1 rounded flex items-center gap-2"
                >
                  {/* Desktop label */}
                  <span className="hidden md:block">
                    {`Next ${selectedRange === "Today" ? "Day" : selectedRange || "Day"}`}
                  </span>

                  {/* Mobile arrow */}
                  <span className="md:hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              )}
            </div>

            {/* 🎯 Switch between views */}
            <div className="w-full overflow-x-auto">
              {activeView === "chart" && (
                <StyledLineChart
                  filteredOrders={filteredOrders}
                  startDate={startDate}
                  endDate={endDate}
                  chartInstanceRef={chartInstanceRef}
                />
              )}

              {activeView === "items" && (
                <ItemsSoldView
                  orders={filteredOrders}
                  startDate={startDate}
                  endDate={endDate}
                  selectedRange={selectedRange}
                  selectedStore={selectedStore}
                />
              )}

              {activeView === "busy" && (
                <BusyTimesView
                  orders={filteredOrders}
                  startDate={startDate}
                  endDate={endDate}
                />
              )}
            </div>

          </div>
        )}

        {startDate && endDate && dayjs(startDate).isSame(dayjs(endDate), 'day') && (
          <div className="flex justify-center my-4">
            <button
              onClick={handleGenerateQuickBooksReport}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Generate QuickBooks Report
            </button>
          </div>
        )}

        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">Transactions</h2>

          <div className="grid grid-cols-5 gap-4 font-semibold border-b pb-2">
            <div>Date</div>
            <div>Store</div>
            <div>Payment Method</div>
            <div>Amount</div>
            <div>Details</div>
          </div>

          {filteredOrders
            .slice()
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .slice(0, transactionsToShow)
            .map((order, index) => (
              <div
                key={index}
                className="grid grid-cols-5 gap-4 py-2 border-b items-center text-sm"
              >
                <div>
                  <div className="font-medium">
                    {new Date(order.timestamp).toLocaleDateString()}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {new Date(order.timestamp).toLocaleTimeString()}
                  </div>
                </div>

                {/* Store column */}
                <div className="text-sm text-gray-800">
                  {order.store || "Unknown"}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {order.paymentMethods?.map((pm, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <img
                        src={`/icons/${pm.method}.png`}
                        alt={pm.method}
                        title={pm.method}
                        className="h-6 w-6 object-contain"
                      />
                      <span className="text-xs">${pm.amount.toFixed(2)}</span>
                    </div>
                  )) || (
                      <div className="flex items-center gap-1">
                        <img
                          src={`/icons/${order.paymentMethod || 'Unknown'}.png`}
                          alt={order.paymentMethod || 'Unknown'}
                          title={order.paymentMethod || 'Unknown'}
                          className="h-6 w-6 object-contain"
                        />
                        <span className="text-xs">${parseFloat(order.grandTotal || 0).toFixed(2)}</span>
                      </div>
                    )}
                </div>

                <div>${parseFloat(order.grandTotal).toFixed(2)}</div>

                <div>
                  <button
                    onClick={() => handleViewDetails(order)}
                    className="px-3 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 text-xs"
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}

          {transactionsToShow < filteredOrders.length && (
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={handleLoadMore}
                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
              >
                Load More
              </button>
              <button
                onClick={handleLoadAll}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Load All
              </button>
            </div>
          )}

        </div>

        {loading && <p className="text-center mt-8">Loading orders...</p>}
        {!loading && filteredOrders.length === 0 && (
          <p className="text-center mt-8">No orders found for the selected filters.</p>
        )}

        {showDetailsModal && selectedOrder && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            onClick={closeDetails}
            onKeyDown={(e) => e.key === "Escape" && closeDetails()}
            tabIndex={-1}
          >
            <div
              className="bg-white p-8 rounded-lg w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >


              {/* Close Button */}
              <button
                onClick={closeDetails}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>

              {/* Logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/LOGO tifsu biscuits DARK.png"
                  alt="TIFSU Logo"
                  className="h-16 object-contain"
                />
              </div>

              {/* Order Details */}
              <h2 className="text-xl font-bold mb-4 text-center">Order Receipt</h2>

              <div className="space-y-2 text-sm">
                <p><strong>Date:</strong> {new Date(selectedOrder.timestamp).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {new Date(selectedOrder.timestamp).toLocaleTimeString()}</p>
                <div className="space-y-1">
                  <strong>Payment Methods:</strong>
                  <ul>
                    {selectedOrder.paymentMethods?.map((pm, idx) => (
                      <li key={idx} className="flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <img
                            src={`/icons/${pm.method}.png`}
                            alt={pm.method}
                            className="h-5 w-5 object-contain"
                          />
                          {pm.method}
                        </span>
                        <span>${pm.amount.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>


                {/* Subtotals and taxes */}
                <p><strong>Subtotal:</strong> ${parseFloat(selectedOrder.subtotal).toFixed(2)}</p>
                <p><strong>GST:</strong> ${parseFloat(selectedOrder.gst).toFixed(2)}</p>
                <p><strong>QST:</strong> ${parseFloat(selectedOrder.qst).toFixed(2)}</p>

                {/* ✅ Show Discount ONLY if it exists and discountValue > 0 */}
                {(selectedOrder.discountValue && parseFloat(selectedOrder.discountValue) > 0) && (
                  <p>
                    <strong>Discount:</strong> {selectedOrder.discount}
                    {` (-$${parseFloat(selectedOrder.discountValue).toFixed(2)})`}
                  </p>
                )}

                <p><strong>Grand Total:</strong> ${parseFloat(selectedOrder.grandTotal).toFixed(2)}</p>

                {/* Itemized List */}
                <div className="mt-4">
                  <h3 className="font-semibold mb-2">Items:</h3>
                  <ul className="space-y-1">
                    {(() => {
                      const grouped = {};
                      selectedOrder.items.forEach(item => {
                        const key = item.name;
                        if (!grouped[key]) {
                          grouped[key] = { ...item, quantity: 0, total: 0 };
                        }
                        grouped[key].quantity += item.quantity;
                        grouped[key].total += parseFloat(item.total);
                      });

                      return Object.entries(grouped).map(([name, item], idx) => (
                        <li key={idx} className="flex justify-between">
                          <span>{item.quantity}x {item.name}</span>
                          <span>${item.total.toFixed(2)}</span>
                        </li>
                      ));
                    })()}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div >
  );

};

export default SalesReportPage;
